import workingHoursService from "@/services/workingHoursService";
import { rulesActiveOnDate } from "@/utils/scheduleResolver";
import { slotMinutes } from "@/utils/workingHoursFormat";

// Range cache + in-flight dedup for the batch branch-schedule resolver. Kept as
// a plain module-scoped Map (NOT Vuex state) so it never disturbs the per-doctor
// editor state and isn't made deeply reactive. Each entry is a Promise<members[]>
// (the in-flight or resolved fetch), keyed by "clinic|branch|from|to".
//
// QueueManage (day board) and DoctorScheduleCalendar (month overview) both load
// on the same page with overlapping ranges; the day range is fully contained in
// the month range. Aligning both callers on the month range (see QueueManage)
// makes their keys collide so the second caller reuses the first's in-flight
// promise — one network call instead of two. The containment branch below also
// serves any narrower request from an already-resolved wider range.
const branchScheduleCache = new Map();

const cacheKeyOf = ({ clinicUrl, branchUrl, from, to }) =>
  `${clinicUrl}|${branchUrl}|${from}|${to}`;

// Slice each member's `days` to the inclusive [from, to] window. Used to serve a
// narrower request from a cached wider range (dates are "YYYY-MM-DD", so string
// comparison is chronological).
const sliceMembersToRange = (members, from, to) =>
  (members || []).map((m) => ({
    ...m,
    days: (m.days || []).filter((d) => d.date >= from && d.date <= to),
  }));

const RRULE_DAY_TO_LABEL = {
  MO: "จันทร์",
  TU: "อังคาร",
  WE: "พุธ",
  TH: "พฤหัสบดี",
  FR: "ศุกร์",
  SA: "เสาร์",
  SU: "อาทิตย์",
};

const bydayOf = (rruleStr) =>
  ((rruleStr || "").match(/BYDAY=([A-Z,]+)/) || [])[1] || "";

// Short header summary of the recurring rules, e.g. "จันทร์, พุธ 09:00–17:00".
const formatRecurringSummary = (rules) => {
  if (!rules || rules.length === 0) return null;
  return rules
    .map((r) => {
      const labels = bydayOf(r.rruleStr)
        .split(",")
        .filter(Boolean)
        .map((d) => RRULE_DAY_TO_LABEL[d] || d)
        .join(", ");
      return `${labels} ${(r.startTime || "").slice(0, 5)}–${(
        r.endTime || ""
      ).slice(0, 5)}`;
    })
    .join(" · ");
};

const moduleWorkingHours = {
  namespaced: true,
  state: {
    branchMemberId: null,
    doctorName: null,
    rules: [], // raw recurring rules (from the branch member list)
    overrides: [], // raw per-date overrides (type: leave | shift)
    schedule: null, // { days:[{date,slots,overrideMarkers}], totalMinutes } from the resolver
    lastQuery: null, // { clinicUrl, branchUrl, branchMemberId, from, to }
  },
  getters: {
    schedule: (s) => s.schedule,
    doctorName: (s) => s.doctorName,
    rules: (s) => s.rules,
    overrides: (s) => s.overrides,
    recurringSummary: (s) => formatRecurringSummary(s.rules),
    // Editable entries for a date: recurring rules active that day + that day's
    // overrides + the server-resolved slots/total for the day.
    dayEntriesFor: (s) => (date) => {
      const day = (s.schedule && s.schedule.days) || [];
      const slots = (day.find((d) => d.date === date) || {}).slots || [];
      return {
        recurring: rulesActiveOnDate(s.rules, date),
        overrides: s.overrides.filter((o) => o.overrideDate === date),
        slots,
        minutes: slotMinutes(slots),
      };
    },
  },
  mutations: {
    SET_BRANCH_MEMBER_ID(state, id) {
      state.branchMemberId = id;
    },
    SET_DOCTOR_NAME(state, name) {
      state.doctorName = name;
    },
    SET_RULES(state, rules) {
      state.rules = rules;
    },
    SET_OVERRIDES(state, overrides) {
      state.overrides = overrides;
    },
    SET_SCHEDULE(state, schedule) {
      state.schedule = schedule;
    },
    SET_LAST_QUERY(state, q) {
      state.lastQuery = q;
    },
  },
  actions: {
    async fetchSchedule(
      { commit, dispatch },
      { clinicUrl, branchUrl, branchMemberId, from, to }
    ) {
      commit("SET_BRANCH_MEMBER_ID", branchMemberId);
      commit("SET_LAST_QUERY", {
        clinicUrl,
        branchUrl,
        branchMemberId,
        from,
        to,
      });

      // Doctor name + raw recurring rules come from the branch member list.
      try {
        const res = await dispatch(
          "moduleBranch/fetchBranchMember",
          { clinicUrl, branchUrl },
          { root: true }
        );
        const list = (res && res.data) || [];
        const member = list.find((m) => m.id === branchMemberId);
        const name =
          (member && member.user && member.user.fullName) ||
          (member && member.fullName) ||
          (member && member.doctorCode) ||
          `#${branchMemberId}`;
        commit("SET_DOCTOR_NAME", name);
        commit("SET_RULES", (member && member.doctorWorkingHours) || []);
      } catch (err) {
        commit("SET_DOCTOR_NAME", `#${branchMemberId}`);
        commit("SET_RULES", []);
      }

      // Resolved per-day schedule + raw overrides from the resolver endpoint.
      const data = await workingHoursService.getSchedule({
        clinicUrl,
        branchUrl,
        branchMemberId,
        from,
        to,
      });
      commit("SET_OVERRIDES", (data && data.overrides) || []);
      commit("SET_SCHEDULE", {
        days: (data && data.days) || [],
        totalMinutes: (data && data.totalMinutes) || 0,
      });
    },

    // Batch month-overview fetch for ALL doctors in the branch. Returns the
    // resolved `members[]` directly to the caller (the overview component holds
    // it locally) rather than storing in module state, so the per-doctor editor
    // state above is never disturbed.
    // -> [{ branchMemberId, days:[{date,slots,overrideMarkers}] }]
    async fetchBranchSchedule(_ctx, { clinicUrl, branchUrl, from, to }) {
      const key = cacheKeyOf({ clinicUrl, branchUrl, from, to });

      // Exact range already fetched or in flight -> reuse it (one network call).
      const cached = branchScheduleCache.get(key);
      if (cached) return cached;

      // Narrower request fully contained in an already-resolved wider range
      // (same clinic/branch) -> serve it by slicing, no network call.
      const prefix = `${clinicUrl}|${branchUrl}|`;
      for (const [k, promise] of branchScheduleCache) {
        if (!k.startsWith(prefix)) continue;
        const [, , cFrom, cTo] = k.split("|");
        if (cFrom <= from && cTo >= to) {
          return promise.then((members) =>
            sliceMembersToRange(members, from, to)
          );
        }
      }

      const promise = workingHoursService
        .getBranchSchedule({ clinicUrl, branchUrl, from, to })
        .then((data) => (data && data.members) || [])
        .catch((err) => {
          // Don't cache failures — let the next call retry.
          branchScheduleCache.delete(key);
          throw err;
        });
      branchScheduleCache.set(key, promise);
      return promise;
    },

    // Re-fetch the currently-viewed range (used after any mutation).
    async reload({ dispatch, state }) {
      if (!state.lastQuery) return;
      await dispatch("fetchSchedule", state.lastQuery);
    },

    // Replace-all of the doctor's recurring rules. Callers recompute the full
    // list (create / edit-all / split / delete-series all reduce to this).
    saveRules({ state }, doctorWorkingHours) {
      const { clinicUrl, branchUrl, branchMemberId } = state.lastQuery;
      return workingHoursService.saveRules({
        clinicUrl,
        branchUrl,
        branchMemberId,
        doctorWorkingHours,
      });
    },
    upsertOverride({ state }, override) {
      const { clinicUrl, branchUrl, branchMemberId } = state.lastQuery;
      return workingHoursService.upsertOverride({
        clinicUrl,
        branchUrl,
        branchMemberId,
        override,
      });
    },
    deleteOverride({ state }, overrideId) {
      const { clinicUrl, branchUrl } = state.lastQuery;
      return workingHoursService.deleteOverride({
        clinicUrl,
        branchUrl,
        overrideId,
      });
    },
  },
};

export default moduleWorkingHours;
